import instance from "../xhrService";
import { Address, State } from "./address";
import { Project } from "./project";

const baseUrl = "/api/clients";
const geoDataUrl = `${baseUrl}/geoData`;
const getCountryListUrl = `${baseUrl}/getCountryList`;
const getClientsDocTypesUrl = `${baseUrl}/getClientDocTypes`;
const getClientsProjectTabsUrl = `${baseUrl}/getClientProjectTabs`;
const getClientsPublicViewTabsUrl = `${baseUrl}/getClientPublicViewTabs`;
const getCustomFieldsUrl = `${baseUrl}/getCustomFields`;
const getClientsUrl = (a: number) => `${baseUrl}/${a}`;
const getContactsUrl = (a: number, b?: number) =>
  `${baseUrl}/${a}/contacts${b ? `/${b}` : ""}`;

export type Contact = {
  id: number;
  first_name: string;
  last_name: string;
  name: string;
  email: string;
};

export type Client = {
  id: number;
  name: string;
  default_finding_observation?: string;
  users: Array<any>;
  address: Address;
  precipitation_client_project_flag: boolean;
  custom_tab: boolean,
  tenant_id?: number,
  finding_metric_tracking?: boolean,
  dissolve_finding_images?: boolean,
  enable_photo_metadata_requirement?: boolean,
  forecast_weather?: boolean,
  lock_report_type?: boolean;
  weather_station_type?: string;
  is_tempest_locked?: boolean;
};

export type ClientDoc = {
  client_doc_type_id: string;
  client_dt: object;
  id: number;
  is_checked: boolean;
  name: string
}

type ClientApi = {
  index: (clientIds?: Array<number>) => Promise<Array<Client>>;
  show: (clientId: number) => Promise<Client>;
  getCustomFields: (clientId: number, fields: string[]) => Promise<any>;
  create: (data: Omit<Client, "id">) => Promise<Client>;
  update: (clientId: number, data: Omit<Client, "id">) => Promise<Client>;
  destroy: (clientId: number) => Promise<any>;
  geoData: {
    index: () => Promise<Array<State>>;
  };
  getCountryList: {
    index: () => Promise<Array<State>>;
  };
  projects: {
    index: (clientId: number) => Promise<Array<Project>>;
    updateProjectPrecipitationDetails: (id: number, data: any) => Promise<Project>;
  };
  contacts: {
    create: (clientId: number, userIds: Array<number>) => Promise<any>;
    destroy: (clientId: number, userId: number) => Promise<any>;
  };
  getClientsDocTypes: {
    index: (clientId: number | null) => Promise<Array<ClientDoc>>;
    forProjects: (clientId: number | null) => Promise<Array<ClientDoc>>;
  };
  addNewClientDocTypes: {
    index: (clientId: number, data: any) => Promise<Array<State>>
  };
  deleteCustomClientDocType: {
    index: (clientId: number, data: any) => Promise<Array<any>>
  };
  editCustomClientDocType: {
    index: (clientId: number, id: any, selectedDocTypeName: any) => Promise<Array<any>>
  };
  reorderClientDocType: {
    index: (actionType: string, clientId: number | null, data: any) => Promise<Array<any>>
  };
  getClientsProjectTab: {
    index: (clientId: number) => Promise<Array<State>>;
  };
  getClientsPublicViewTab: {
    index: (clientId: number) => Promise<Array<any>>;
  };
  documentGroups: {
    index: (clientId: number) => Promise<Array<any>>;
    getDocTypeGroupItems: (clientId: number, docTypeGroupId: number) => Promise<any>;
    create: (clientId: number, data: { doc_type_group_ids: number[] }) => Promise<any>;
    delete: (clientId: number, documentGroupIds: number[]) => Promise<any>;
    update: (clientId: number, docTypeGroupId: number, data: {  doc_type_id: number; sort_order: number }[] ) => Promise<any>;
    lock: (clientId: number, docTypeGroupId: number, data: { lock: boolean }) => Promise<any>;
  };
  customDocTypes: {
    index: (clientId: number) => Promise<Array<any>>;
  };
  scheduleDownload: {
    getClientUsers: (clientId: number) => Promise<{ id: number; userClientId: number; name: string; email: string }[]>;
    getConfig: (clientId: number) => Promise<any>;
    saveConfig: (clientId: number, data: any) => Promise<any>;
    stop: (clientId: number) => Promise<any>;
    resendLink: (clientId: number, historyId: number) => Promise<any>;
    download: (clientId: number, historyId: number) => Promise<string>;
  };
};

export const clientApi: ClientApi = {
  /**
   * By default route will return all clients.
   * Pass an array of client ids to retrieve only those clients
   */
  index: clientIds => {
    return instance
      .get(
        baseUrl,
        clientIds
          ? {
            params: {
              clientIds,
            },
          }
          : {},
      )
      .then(res => res.data);
  },
  show: clientId => {
    return instance.get(getClientsUrl(clientId)).then(res => res.data);
  },
  getCustomFields: (clientId, fields) => {
    const params = new URLSearchParams();

    fields.forEach(field => params.append("fields", field));

    return instance
      .get(`${getCustomFieldsUrl}/${clientId}`, {params})
      .then(res => res.data);
  },
  create: data => instance.post(baseUrl, data).then(res => res.data),
  update: (clientId, data) => {
    return instance.put(getClientsUrl(clientId), data).then(res => res.data);
  },
  destroy: clientId => {
    return instance.delete(getClientsUrl(clientId)).then(res => res.data);
  },

  geoData: {
    index: () => instance.get(geoDataUrl).then(res => res.data),
  },

  getCountryList: {
    index: () => instance.get(getCountryListUrl).then(res => res.data),
  },

  projects: {
    index: clientId =>
      instance.get(`/api/clients/${clientId}/projects`).then(res => res.data),

    updateProjectPrecipitationDetails: (id: number, data: any) => {
      return instance.put(`/api/clients/updateProjectPrecipitationDetails/projects/${id}`, data).then(res => res.data);
    }
  },

  contacts: {
    /**
     * @param {number} clientId
     * @param {number[]} userIds
     */
    create: (clientId, userIds) => {
      return instance
        .post(getContactsUrl(clientId), { userIds })
        .then(res => res.data);
    },

    destroy: (clientId, userId) => {
      return instance
        .delete(getContactsUrl(clientId, userId))
        .then(res => res.data);
    },
  },

  getClientsDocTypes: {
    index: (clientId) => instance.get(`${getClientsDocTypesUrl}/${clientId}`).then(res => {
      return res.data
    }),
    forProjects: (clientId) => instance.get(`${getClientsDocTypesUrl}/${clientId}`).then(res => {
      return res.data.length && res.data.filter((ele: any) => ele.is_checked)
    }),
  },

  addNewClientDocTypes: {
    index: (clientId, data) => instance.post(`${baseUrl}/${clientId}/customClientDocType`, data).then(res => {
      return res.data
    }),
  },

  deleteCustomClientDocType: {
    index: (clientId, docTypeId) => instance.delete(`${baseUrl}/${clientId}/customClientDocType/${docTypeId}`).then(res => {      
      return res.data
    }),
  },

  editCustomClientDocType: {
    index: (clientId, docTypeId, selectedDocTypeName) => instance.put(`${baseUrl}/${clientId}/customClientDocType/${docTypeId}`, {updatedName: selectedDocTypeName}).then(res => {            
      return res.data
    }),
  },

  reorderClientDocType: {
    index: (actionType, clientId, data) => instance.put(`${baseUrl}/${clientId}/reorderClientDocType`, { actionType: actionType, data: data }).then(res => {            
      return res.data
    }),
  },
  
  getClientsProjectTab: {
    index: (clientId) => instance.get(`${getClientsProjectTabsUrl}/${clientId}`).then(res => res.data),
  },

  getClientsPublicViewTab: {
    index: (clientId) => instance.get(`${getClientsPublicViewTabsUrl}/${clientId}`).then(res => res.data),
  },

  documentGroups: {
    index: (clientId) => 
      instance.get(`${baseUrl}/${clientId}/doc-type-groups`).then(res => res.data),
    getDocTypeGroupItems: (clientId, docTypeGroupId) =>
      instance.get(`${baseUrl}/${clientId}/doc-type-groups/${docTypeGroupId}`).then(res => res.data),
    
    create: (clientId, data) => 
      instance.post(`${baseUrl}/${clientId}/doc-type-groups/bulk`, data).then(res => res.data),
    
    delete: (clientId, documentGroupIds) => 
      instance.delete(`${baseUrl}/${clientId}/doc-type-groups`, { 
        data: { documentGroupIds } 
      }).then(res => res.data),
    
    update: (clientId, docTypeGroupId, data) => 
      instance.put(`${baseUrl}/${clientId}/doc-type-groups/${docTypeGroupId}`, data).then(res => res.data),
    lock: (clientId, docTypeGroupId, data) => 
      instance.put(`${baseUrl}/${clientId}/doc-type-groups/${docTypeGroupId}/lock`, data).then(res => res.data),
  },

  customDocTypes: {
    index: (clientId) =>
      instance.get(`${baseUrl}/${clientId}/custom-doc-types`).then(res => res.data),
  },

  scheduleDownload: {
    getClientUsers: (clientId) =>
      instance.get(`${baseUrl}/${clientId}/schedule-download/users`).then(res => res.data.users),

    getConfig: (clientId) =>
      instance.get(`${baseUrl}/${clientId}/schedule-download`).then(res => res.data),

    saveConfig: (clientId, data) =>
      instance.post(`${baseUrl}/${clientId}/schedule-download`, data).then(res => res.data),

    stop: (clientId) =>
      instance.put(`${baseUrl}/${clientId}/schedule-download/stop`).then(res => res.data),

    resendLink: (clientId, historyId) =>
      instance.post(`${baseUrl}/${clientId}/schedule-download/history/${historyId}/resend`).then(res => res.data),

    download: (clientId, historyId) =>
      instance.get(`${baseUrl}/${clientId}/schedule-download/history/${historyId}/download`).then(res => res.data.url as string),
  },
};

export default clientApi;
